
import OsanoFooterLink from '@root/joinroot.com/src/components/footer/osano-footer-link';
import PageTemplate from '@root/joinroot.com/src/components/pages/page-template';
import PrivacyActRequestForm from '@root/joinroot.com/src/components/privacy/privacy-act-request-form';
import PrivacyActRequestFormConfirmation from '@root/joinroot.com/src/components/privacy/privacy-act-request-form-confirmation';
import PrivacyActRequestFormRetry from '@root/joinroot.com/src/components/privacy/privacy-act-request-form-retry';
import PrivacyActRequestFormSuccess from '@root/joinroot.com/src/components/privacy/privacy-act-request-form-success';
import PropTypes from '@root/vendor/prop-types';
import Responsive from '@root/core/src/utils/responsive';
import Theme from '@root/gatsby-contentful-core/src/components/brand/theme';
import ViewPrivacyPolicyCta from '@root/joinroot.com/src/components/privacy/view-privacy-policy-cta';
import environment from '@root/core/src/utils/environment';
import submitPrivacyActRequestForm from '@root/joinroot.com/src/api/submit-privacy-act-request-form';
import useAnalytics from '@root/core/src/hooks/use-analytics';
import useSafeImperativeNetworkRequest from '@root/core/src/hooks/use-safe-imperative-network-request';
import { Colors, StyleSheet } from '@root/core/src/utils/styles';
import { PageContent } from '@root/gatsby-contentful-core/src/components/layout/content';
import { useState } from '@root/vendor/react';

const FormStates = {
  CONFIRMING: 'confirming',
  FAILED: 'failed',
  FILLING: 'filling',
  SUBMITTING: 'submitting',
  SUCCESS: 'success',
};

export default function DataCollectionPrivacyPage({ location }) {
  const [formValues, setFormValues] = useState({});
  const [formState, setFormState] = useState(FormStates.FILLING);
  const { trackClick } = useAnalytics('DATA_COLLECTION_PRIVACY_PAGE');
  const [submitFormData] = useSafeImperativeNetworkRequest(submitPrivacyActRequestForm);

  const formSubmit = (values) => {
    trackClick('PRIVACY_ACT_REQUEST_FORM_SUBMITTED');
    setFormValues(values);
    setFormState(FormStates.CONFIRMING);
  };

  // eslint-disable-next-line root/prevent-use-of-window-location
  const formCancelPress = () => window.location.reload();

  const formConfirmationPress = () => {
    submitFormData(formValues).then((response) => {
      setFormState(response.isSuccess() ? FormStates.SUCCESS : FormStates.FAILED);
    });

    setFormState(FormStates.SUBMITTING);
  };

  // eslint-disable-next-line root/prevent-use-of-window-location
  const formDonePress = () => window.location.reload();

  const formRetryPress = () => {
    submitFormData(formValues).then((response) => {
      setFormState(response.isSuccess() ? FormStates.SUCCESS : FormStates.FAILED);
    });

    setFormState(FormStates.SUBMITTING);
  };

  const renderModal = () => {
    switch (formState) {
    case FormStates.CONFIRMING:
    case FormStates.SUBMITTING:
      return (
        <PrivacyActRequestFormConfirmation
          deleteAccountCredentials={formValues.deleteAccountCredentials}
          deleteData={formValues.deleteData}
          onCancelPress={formCancelPress}
          onConfirmationPress={formConfirmationPress}
          optOut={formValues.optOut}
          submitting={formState === FormStates.SUBMITTING}
        />
      );
    case FormStates.FAILED:
      return (
        <PrivacyActRequestFormRetry
          onCancelPress={formCancelPress}
          onRetryPress={formRetryPress}
        />
      );
    case FormStates.SUCCESS:
      return (
        <PrivacyActRequestFormSuccess
          onCancelPress={formCancelPress}
          onDonePress={formDonePress}
        />
      );
    case FormStates.FILLING:
    default:
      return null;
    }
  };

  return (
    <PageTemplate
      deIndexPage
      location={location}
      pageCanonical={'https://www.joinroot.com/privacy/data-collection/'}
      pageTitle={'Data Collection and Deletion at Root'}
    >
      <header css={styles.header}>
        <div css={styles.contentWrapper}>
          <h1 css={styles.h1}>Data Collection and Deletion at Root</h1>
        </div>
      </header>
      <ViewPrivacyPolicyCta />
      <PageContent>
        <h2 css={styles.subheader}>Control your data sharing, data storage, and account access</h2>

        <section css={styles.legalCopy}>
          <p>
            As an insurer, Root is exempt from most state privacy laws that provide specific rights regarding
            personal data. In certain states, California and Minnesota amongst others, such laws may apply to
            the limited data that Root collects that is not associated with your insurance application, policy,
            or account. Root does not offer California and Minnesota residents the ability to request most
            rights under state law because we are unable to verify or authenticate individuals adequately based
            on the limited data we collect that is not associated with an application, policy, or account.
          </p>

          <p>
            <strong>Opting Out:</strong> Residents of any state, including California and Minnesota, can opt
            out of activities on our websites that may be considered &quot;sales&quot; or &quot;sharing&quot;
            for targeted advertising under state laws and may also be considered processing of information
            for targeted advertising. You can opt out by clicking the &quot;Manage Cookies&quot; link below
            or in our Websites footer and enabling the toggle for &quot;Do Not Sell or Share My Personal
            Information&quot; before clicking &quot;Save&quot;, or see our Privacy Policy for information on
            opting out using the Global Privacy Control.
          </p>

          <p css={styles.manageCookiesLink}>
            {environment.osanoEnabled === 'true' && <OsanoFooterLink linkText={'Opt out of data sharing by Managing Cookies here'} />}
          </p>

          <p>
            <strong>Deleting Account Credentials:</strong> Residents of any state can use this form to make a
            request to delete your account credentials.  Certain limitations and exceptions apply.
          </p>
        </section>

        <PrivacyActRequestForm onFormSubmit={formSubmit} />
        {renderModal()}
      </PageContent>
    </PageTemplate>
  );
}

DataCollectionPrivacyPage.propTypes = {
  location: PropTypes.shape({
    search: PropTypes.string.isRequired,
    pathname: PropTypes.string.isRequired,
  }.isRequired),
};

const styles = StyleSheet.create({
  header: {
    display: 'flex',
    alignItems: 'center',
    background: Colors.black(),
    color: Colors.white(),
    padding: '60px 20px 110px',
    textAlign: 'center',
    ...Responsive.md({
      padding: '80px 60px 130px',
    }),
    ...Responsive.lg({
      padding: '110px 120px 160px',
    }),
  },
  contentWrapper: {
    maxWidth: 335,
    margin: 'auto',
    ...Responsive.sm({
      maxWidth: 500,
    }),
    ...Responsive.md({
      maxWidth: 700,
    }),
    ...Responsive.lg({
      maxWidth: 1200,
    }),
  },
  h1: {
    ...Theme.heading2(),
    marginBottom: 15,
    color: Colors.white(),
  },
  subcopy: {
    ...Theme.subhead2Light(),
    margin: '25px auto',
    color: Colors.white(),
    ...Responsive.lg({
      width: 900,
      margin: '30px auto',
    }),
  },
  subheader: {
    ...Theme.subhead1Light(),
    fontSize: 36,
    textAlign: 'center',
    margin: 30,
  },
  manageCookiesLink: {
    textAlign: 'center',
    '& a': {
      fontSize: 20,
    },
  },
  legalCopy: {
    marginBottom: 40,
    '& p': {
      margin: '20px 0',
      color: Colors.gray50(),
      fontSize: 20,
    },
  },
});
